import { Checkbox, Container, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, useEffect, useState } from 'react';

import { Page } from '../../components';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { AdminTable } from '../../components/table/AdminTable';
import { FileUseTypeAPI, getEntityTypes, getFileTypes } from '../../fetch';
import { useToastContext } from '../../hooks';
import { FileUseTypeFactory, IDropdownResponse, IFileUseType, Maybe } from '../../models';

export const FileUseTypes: FC = () => {
  // Non-state
  const { showToast } = useToastContext();

  // Local state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  // If not false, this is the row the user seeks to delete
  const [isDeletingFileUseType, setIsDeletingFileUseType] = useState<false | IFileUseType>(false);

  // Local state from the network
  const [rows, setRows] = useState<IFileUseType[]>([]);

  const removeRow = (row: IFileUseType): void => {
    setRows((state: IFileUseType[]) => {
      const index = state.indexOf(row);
      const newState = state.slice();
      // Cut out the deleted row
      newState.splice(index, 1);
      return newState;
    });
  };

  const [fileTypes, setFileTypes] = useState<Maybe<IDropdownResponse[]>>(null);
  const [isLoadingFileTypes, setIsLoadingFileTypes] = useState(false);
  const fetchFileTypes = async () => {
    try {
      setIsLoadingFileTypes(true);
      const res = await getFileTypes();
      setFileTypes(res);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingFileTypes(false);
    }
  };

  const [entityTypes, setEntityTypes] = useState<Maybe<IDropdownResponse[]>>(null);
  const [isLoadingEntityTypes, setIsLoadingEntityTypes] = useState(false);
  const fetchEntityTypes = async () => {
    try {
      setIsLoadingEntityTypes(true);
      const res = await getEntityTypes();
      setEntityTypes(res);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingEntityTypes(false);
    }
  };

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(rows.some(row => row.name === null || row.name === ''));
  }, [rows]);

  const columns = [
    {
      accesor: 'fileUse',
      Header: 'File Use',
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: IFileUseType; index: number } };
      }) => {
        const [fileUseTypeName, setFileUseTypeName] = useState(original.name);

        return (
          <Box>
            <TextField
              fullWidth
              inputProps={{
                className: 'file-use-type-name-input',
              }}
              placeholder="File Use Type Name"
              value={fileUseTypeName}
              required
              autoFocus
              onChange={event => {
                setFileUseTypeName(event.target.value);
                setRows(state => {
                  const index = state.indexOf(original);

                  // Enable save button
                  if (state[index].name !== event.target.value) {
                    setHasChanges(true);
                    state[index].isEdited = true;
                  }

                  // Store changes in preparation for batch save
                  state[index].name = event.target.value;
                  return state;
                });
              }}
              error={fileUseTypeName === ''}
            />
          </Box>
        );
      },
    },
    {
      accesor: 'name',
      Header: 'File Type',
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: IFileUseType; index: number } };
      }) => {
        const [fileType, setFileType] = useState<Maybe<number | string>>(original.fileType ?? '');
        return (
          <TextField
            select
            fullWidth
            variant="outlined"
            required
            size="small"
            disabled={isLoadingFileTypes}
            placeholder="File Type"
            value={fileType}
            onChange={({ target: { value } }) => {
              setFileType(value);
              setRows(state => {
                const index = state.indexOf(original);

                // Enable save button
                if (state[index].fileType !== value) {
                  setHasChanges(true);
                  state[index].isEdited = true;
                }

                // Store changes in preparation for batch save
                state[index].fileType = value;

                return state;
              });
            }}
            error={!fileType}
          >
            {fileTypes &&
              fileTypes.map((fileType, index) => {
                return (
                  <MenuItem key={`${index + 1}`} value={fileType.value}>
                    {fileType.description}
                  </MenuItem>
                );
              })}
          </TextField>
        );
      },
    },
    {
      accesor: 'entityType',
      Header: 'Entity Type',
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: IFileUseType; index: number } };
      }) => {
        const [entityType, setEntityType] = useState<Maybe<number | string>>(
          original.entityType ?? null
        );
        return (
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            disabled={isLoadingEntityTypes}
            placeholder="Entity Type"
            value={entityType}
            onChange={({ target: { value } }) => {
              setEntityType(value);
              setRows(state => {
                const accountIndex = state.indexOf(original);

                // Enable save button
                if (state[accountIndex].entityType !== value) {
                  setHasChanges(true);
                  state[accountIndex].isEdited = true;
                }

                // Store changes in preparation for batch save
                state[accountIndex].entityType = value;
                return JSON.parse(JSON.stringify(state));
              });
            }}
          >
            <MenuItem key={`${0}`} value={''}>
              {'All Entities'}
            </MenuItem>
            {entityTypes &&
              entityTypes.map((entityType, index) => {
                return (
                  <MenuItem key={`${index + 1}`} value={entityType.value}>
                    {entityType.description}
                  </MenuItem>
                );
              })}
          </TextField>
        );
      },
    },
    {
      accesor: '',
      Header: 'Allow Multiples',
      Cell: ({
        cell: {
          row: { original, index },
        },
      }: {
        cell: { row: { original: IFileUseType; index: number } };
      }) => {
        const [allowMultiples, setAllowMultiples] = useState<boolean>(
          original.allowMultiples ?? false
        );
        return (
          <FormControlLabel
            color="primary"
            control={
              <Checkbox
                checked={allowMultiples}
                id={`allow-multiples-${index}`}
                onChange={({ target: { checked } }) => {
                  setAllowMultiples(checked);
                  setRows(state => {
                    const accountIndex = state.indexOf(original);

                    // Enable save button
                    if (state[accountIndex].allowMultiples !== checked) {
                      setHasChanges(true);
                      state[accountIndex].isEdited = true;
                    }

                    // Store changes in preparation for batch save
                    state[accountIndex].allowMultiples = checked;
                    return state;
                  });
                }}
              />
            }
            label=""
          />
        );
      },
    },
  ];

  useEffect(() => {
    fetchOnMount();
    fetchFileTypes();
    fetchEntityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOnMount = async () => {
    setIsLoading(true);
    try {
      const fileUseTypes: IFileUseType[] = await FileUseTypeAPI.getAll();
      setRows(fileUseTypes);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : displayFailure('get', 'file use types');
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page title="File Use Types">
      <Container maxWidth={false} disableGutters>
        <AdminTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          hasChanges={hasChanges}
          isSaving={isSaving}
          isSaveDisabled={hasError}
          onAddFocusClass="file-use-input"
          onAdd={() => {
            // Add new empty row
            setRows(state =>
              state.concat(FileUseTypeFactory.getDefault(parseInt(`${fileTypes?.[0].value}`) ?? 1))
            );
          }}
          onSave={async function upsertFileUseTypes() {
            setIsSaving(true);
            try {
              const newRows: IFileUseType[] = await FileUseTypeAPI.upsert(rows);

              // After successful update, update UI
              setRows(newRows);

              // Reset save button to disabled
              setHasChanges(false);

              showToast('success', 'Successfully saved  file use types.');
            } catch (error: any) {
              const message = error?.Detail
                ? error?.Detail
                : displayFailure('update', 'file use types');
              showToast('error', message);
            } finally {
              setIsSaving(false);
            }
          }}
          onDelete={(original, _index) => {
            if (original.fileUseTypeId === FileUseTypeFactory.getDefault().fileUseTypeId) {
              removeRow(original);
              showToast('info', 'Removed unsaved file use type.');
            } else {
              setIsDeletingFileUseType(original);
            }
          }}
        />
      </Container>

      {/** ConfirmationDialogue */}
      {!!isDeletingFileUseType && (
        <ConfirmationDialogue
          open
          id="delete-file-use-type"
          title={`Delete File Use Type ${isDeletingFileUseType.name}`}
          onClose={() => setIsDeletingFileUseType(false)}
          onConfirm={async function deleteFileUseType() {
            // Check if modal is open and has proper data
            if (!isDeletingFileUseType) {
              return;
            }
            // If this is a new, not-yet-saved entry, it does not exist in the DB
            if (
              isDeletingFileUseType.fileUseTypeId === FileUseTypeFactory.getDefault().fileUseTypeId
            ) {
              // So, deletion only consists of removal from local state
              removeRow(isDeletingFileUseType);
            } else {
              // Otherwise this entry already exists, hit the API to delete it
              try {
                await FileUseTypeAPI.delete(isDeletingFileUseType.fileUseTypeId);
                // Update state after only successful delete
                removeRow(isDeletingFileUseType);
                showToast('success', 'Successfully deleted file use type.');
              } catch (error) {
                const message =
                  error instanceof Error
                    ? error.message
                    : displayFailure('delete', 'file use type');
                showToast('error', message);
              }
            }

            // Close modal
            setIsDeletingFileUseType(false);
          }}
        />
      )}
    </Page>
  );
};

function displayFailure(operation: 'get' | 'update' | 'delete', resource: string): string {
  // Verify message ends with a period.
  const resource_ = resource.endsWith('.') ? resource.slice(0, resource.length - 1) : resource;

  return `Failed to ${operation} ${resource_}.`;
}
